import {graphyoConfigError} from "./graphyo-activity";
import {measurementEngineId,decodeExperiment} from './measurement-engine';
import {chemicalLaboratoryId} from './chemical-lab-config';
import {graphConfigErrors} from './interactive-graph-model';
import {correctionGraphErrors} from './correction-graph';
import {titrationDiagramError} from './titration-diagram';
import { checkpointAnchorId, checkpointEvidenceIds, isEvidenceBlock } from "./interactive-activity-checkpoints";
import Ajv2020 from "ajv/dist/2020";
import {resourceReferenceError,resourceForAsset} from "./interactive-resources";
import {validateResourceConfig} from './resource-config';
import {isExperiment} from './resources/measurement-contract';
import definitionSchema from "../docs/interactive-activity-v1.schema.json";
import {
  InteractiveActivityDefinition,
  interactiveCompetencyIds,
  validateInteractiveActivity,
} from "./interactive-activity";

export type InteractiveActivityMetadata = {
  title: string;
  schoolLevel: string;
  theme: string;
  chapter: string;
  coefficient: number;
  competencyIds: string[];
};
export type InteractiveActivityPackage = {
  format: "competenceo-interactive-activity";
  version: 1;
  activity: InteractiveActivityMetadata;
  interactive: InteractiveActivityDefinition;
};
const ajv = new Ajv2020({ allErrors: true, strict: false });
const checkStructure = ajv.compile({
  type: "object",
  required: ["format", "version", "activity", "interactive"],
  properties: {
    format: { const: "competenceo-interactive-activity" },
    version: { const: 1 },
    activity: {
      type: "object",
      required: [
        "title",
        "schoolLevel",
        "theme",
        "chapter",
        "coefficient",
        "competencyIds",
      ],
      properties: {
        title: { type: "string", minLength: 1 },
        schoolLevel: { type: "string", minLength: 1 },
        theme: { type: "string" },
        chapter: { type: "string", minLength: 1 },
        coefficient: { type: "number", exclusiveMinimum: 0 },
        competencyIds: {
          type: "array",
          maxItems: 4,
          uniqueItems: true,
          items: { type: "string" },
        },
      },
    },
    interactive: definitionSchema,
  },
});
export function validateInteractiveActivityPackage(
  value: unknown,
  allowedCompetencyIds?: string[],
): string[] {
  if (!checkStructure(value))
    return (checkStructure.errors ?? []).map(
      (e) =>
        `${e.instancePath || "/"}${e.keyword === "required" ? `/${e.params.missingProperty}` : ""} : ${e.message}`,
    );
  const pkg = value as InteractiveActivityPackage;
  const errors = validateInteractiveActivity(
    pkg.interactive,
    pkg.activity.competencyIds,
  );
  const used = new Set(interactiveCompetencyIds(pkg.interactive));
  for (const asset of pkg.interactive.assets) {
    const error = resourceReferenceError(asset);
    if (error) errors.push(`/interactive/assets/${asset.id} : ${error}`);
  }
  for (const key of ["title", "schoolLevel", "chapter"] as const)
    if (!pkg.activity[key].trim())
      errors.push(`/activity/${key} : ce champ est obligatoire.`);
  pkg.activity.competencyIds.forEach((id) => {
    if (!used.has(id))
      errors.push(
        `/activity/competencyIds : ${id} n’est associé à aucun bloc ou groupe.`,
      );
    if (allowedCompetencyIds && !allowedCompetencyIds.includes(id))
      errors.push(
        `/activity/competencyIds : compétence absente du référentiel CompetenceO : ${id}.`,
      );
  });
  const assets = new Map(pkg.interactive.assets.map((a) => [a.id, a]));
  const blocks = new Map(pkg.interactive.blocks.map((b) => [b.id, b]));
  if (assets.size !== pkg.interactive.assets.length)
    errors.push("/interactive/assets : identifiants dupliqués.");
  for (const [i, block] of pkg.interactive.blocks.entries()) {
    const path = `/interactive/blocks/${i}`;
    if (block.type==='question' && block.progressTable?.equationSourceBlockId) {
      const source=blocks.get(block.progressTable.equationSourceBlockId);
      if(source?.type!=='question'||!source.responseFields?.equation||block.progressTable.species.some(s=>!source.responseFields!.fields.some(f=>f.id===s.id)))
        errors.push(`${path}/progressTable/equationSourceBlockId : question de coefficients incompatible.`);
    }
    if(block.type==='question' && block.progressTable && block.responseFields)
      errors.push(`${path} : choisir un tableau ou des champs structurés, pas les deux.`);
    if(block.type==="simulation"||block.type==="animation") {
      const asset=assets.get(block.assetId??"");const resource=asset&&resourceForAsset(asset);
      if(block.resourceId===measurementEngineId||block.resourceId===chemicalLaboratoryId&&block.measurementTargetBlockId){
        const target=blocks.get(block.measurementTargetBlockId??'');
        if(!target||target.type!=='measurement_table'||!target.resourceMeasurementTable||block.lockAfterCompletion)errors.push(path+' : le moteur de mesure exige un tableau lié et conserve ses actions sans verrouillage de manipulation.');
        else try{const c=decodeExperiment(block.resourceConfig!);if(target.measurementTableKind!==c.measurement||target.acquisitionOnly&&c.transfer==='manual')errors.push(path+' : adapter le type du tableau à la grandeur et autoriser la saisie manuelle.');}catch{/* Configuration errors reported below. */}
      }
      if(block.measurementTargetBlockId){const target=blocks.get(block.measurementTargetBlockId);if(!resource||(!isExperiment(resource.resourceId)&&resource.resourceId!==chemicalLaboratoryId)||!target||!['question','measurement_table'].includes(target.type)||!('resourceMeasurementTable' in target)||!target.resourceMeasurementTable)errors.push(`${path} : cible de mesures invalide.`);else if(block.visible!==false&&target.visible===false)errors.push(`${path} : carnet cible masqué.`);}
      if(block.resourceConfig!==undefined||block.resourceId!==undefined||block.resourceVersion!==undefined) {
        if(!resource?.parameterSchema||block.resourceId!==resource.resourceId||block.resourceVersion!==resource.version) errors.push(`${path} : référence configurable incohérente.`);
        else errors.push(...validateResourceConfig(block.resourceId!,block.resourceConfig??{}).errors.map(e=>`${path}/resourceConfig : ${e}`));
      }
    }
    if ("assetId" in block && block.assetId && !assets.has(block.assetId))
      errors.push(`${path}/assetId : ressource inconnue ${block.assetId}.`);
    if (
      "assetId" in block &&
      block.assetId &&
      assets.has(block.assetId) &&
      assets.get(block.assetId)!.kind !== block.type
    )
      errors.push(
        `${path}/assetId : le type de ressource doit être ${block.type}.`,
      );
    if ("graphyo" in block && block.graphyo) {
      if(block.type!=="graph_task" || block.graphConfig || block.sourceTableBlockId) errors.push(`${path} : Graphyo doit être un bloc autonome sans ancien graphique ni tableau source.`);
      if(graphyoConfigError(block.graphyo))errors.push(`${path} : configuration Graphyo invalide.`);
      if(new Set(block.graphyo.columns.map(c=>c.symbol)).size!==block.graphyo.columns.length) errors.push(`${path} : symboles Graphyo dupliqués.`);
    }
    if (block.type === "question") {
      if(block.correctionGraphConfig)errors.push(...correctionGraphErrors(block.correctionGraphConfig).map(e=>`${path}/correctionGraphConfig : ${e}`));
      if(block.correctionDiagram){const error=titrationDiagramError(block.correctionDiagram);if(error)errors.push(`${path}/correctionDiagram : ${error}`);}
      if(block.selectionMode!==undefined&&!['single','multiple'].includes(block.selectionMode))errors.push(`${path}/selectionMode : choix invalide.`);
      if(block.selectionMode==='single'&&block.correctionMode!=='teacher'&&block.options?.filter(o=>o.correct).length!==1)errors.push(`${path}/options : une seule bonne réponse requise.`);
      if(block.measurementSourceBlockId){const source=blocks.get(block.measurementSourceBlockId);if(source?.type!=='measurement_table'||source.visible===false)errors.push(`${path} : carnet source absent ou masqué.`);}
      if (
        block.options &&
        new Set(block.options.map((o) => o.id)).size !== block.options.length
      )
        errors.push(`${path}/options : identifiants dupliqués.`);
      if (
        block.answerType === "true_false" &&
        block.correctionMode !== "teacher" &&
        (block.correction?.acceptedAnswers?.length !== 1 ||
          !["true", "false"].includes(block.correction.acceptedAnswers[0]))
      )
        errors.push(
          `${path}/correction/acceptedAnswers : définir true ou false.`,
        );
      if (
        ["short_text", "formula"].includes(block.answerType) &&
        block.correctionMode !== "teacher" &&
        !block.correction?.acceptedAnswers?.some((answer) => answer.trim())
      )
        errors.push(
          `${path}/correction/acceptedAnswers : réponse attendue manquante.`,
        );
      if (block.answerType === "open" && block.correctionMode === "automatic")
        errors.push(
          `${path}/correctionMode : une réponse développée nécessite une correction professeur ou hybride.`,
        );
    }
    if (block.type === "measurement_table") {
      if(block.concentrationSource){
        const link=block.concentrationSource,source=blocks.get(link.blockId);
        if(source?.type!=='measurement_table'||source.resourceMeasurementTable||source.id===block.id||!source.columns.some(c=>c.id===link.solutionColumnId)||!source.columns.some(c=>c.id===link.concentrationColumnId&&c.input==='number'))errors.push(`${path}/concentrationSource : tableau de calcul ou colonnes incompatibles.`);
        else if(block.visible!==false&&source.visible===false)errors.push(`${path}/concentrationSource : tableau de calcul masqué.`);
      }
      if(block.calibrationSeries?.some(s=>s.preparationSolution)&&!block.concentrationSource)errors.push(`${path}/concentrationSource : tableau de calcul requis.`);
      if(block.validateBeforeGraph&&!block.calibrationSeries)errors.push(`${path}/validateBeforeGraph : une gamme étalon est requise.`);
      if (new Set(block.columns.map((c) => c.id)).size !== block.columns.length)
        errors.push(`${path}/columns : identifiants dupliqués.`);
      if ((block.maxRows ?? Infinity) < (block.minRows ?? 1))
        errors.push(
          `${path}/maxRows : doit être supérieur ou égal au minimum.`,
        );
      for (const col of block.columns)
        if (
          col.expected?.min !== undefined &&
          col.expected.max !== undefined &&
          col.expected.min > col.expected.max
        )
          errors.push(
            `${path}/columns/${col.id}/expected : intervalle inversé.`,
          );
    }
    if (block.type === "graph_task") {
      if(block.correctionGraphConfig)errors.push(...correctionGraphErrors(block.correctionGraphConfig).map(e=>`${path}/correctionGraphConfig : ${e}`));
      if(block.graphConfig)errors.push(...graphConfigErrors(block.graphConfig).map(e=>`${path}/graphConfig : ${e}`));
      const table = block.sourceTableBlockId
        ? blocks.get(block.sourceTableBlockId)
        : undefined;
      if (block.sourceTableBlockId && table?.type !== "measurement_table")
        errors.push(`${path}/sourceTableBlockId : tableau inconnu.`);
      for (const key of ["xColumnId", "yColumnId"] as const)
        if (
          block[key] &&
          (table?.type !== "measurement_table" ||
            !table.columns.some((c) => c.id === block[key]))
        )
          errors.push(`${path}/${key} : colonne inconnue.`);
    }
    if (block.type === "simulation" || block.type === "animation") {
      if (
        new Set(block.controls?.map((c) => c.id)).size !==
        (block.controls?.length ?? 0)
      )
        errors.push(`${path}/controls : identifiants dupliqués.`);
      for (const c of block.controls ?? [])
        if (c.min !== undefined && c.max !== undefined && c.min >= c.max)
          errors.push(
            `${path}/controls/${c.id} : le maximum doit dépasser le minimum.`,
          );
    }
  }
  for (const [index, block] of pkg.interactive.blocks.entries()) {
    if (block.type !== "question" || block.helpSnapshotSourceBlockId === undefined) continue;
    const sourceIndex = pkg.interactive.blocks.findIndex(b => b.id === block.helpSnapshotSourceBlockId);
    const source = pkg.interactive.blocks[sourceIndex];
    if (!block.hint?.trim() || source?.type !== "question" || sourceIndex >= index || !["open", "short_text"].includes(source.answerType))
      errors.push(`/interactive/blocks/${index}/helpSnapshotSourceBlockId : une proposition écrite antérieure et une aide sont requises.`);
  }
  const rules = pkg.interactive.competencyRules;
  const checkpointIds = new Set<string>();
  for (const [i, checkpoint] of (pkg.interactive.competencyCheckpoints ?? []).entries()) {
    const path = `/interactive/competencyCheckpoints/${i}`;
    if(blocks.get(checkpointAnchorId(checkpoint))?.visible===false)errors.push(`${path} : bloc d’ancrage masqué.`);
    if (checkpointIds.has(checkpoint.id) || blocks.has(checkpoint.id))
      errors.push(`${path}/id : identifiant déjà utilisé.`);
    checkpointIds.add(checkpoint.id);
    if (checkpointAnchorId(checkpoint) && (!blocks.has(checkpointAnchorId(checkpoint)) || (!checkpoint.positionAfterBlockId && blocks.get(checkpointAnchorId(checkpoint))?.type !== "question")))
      errors.push(`${path}/${checkpoint.positionAfterBlockId ? "positionAfterBlockId" : "positionAfterQuestionId"} : bloc d’ancrage inconnu.`);
    if (checkpoint.flowPosition !== undefined &&
        (checkpoint.flowPosition > pkg.interactive.blocks.length ||
         (checkpointAnchorId(checkpoint) && checkpoint.flowPosition !== pkg.interactive.blocks.findIndex(b => b.id === checkpointAnchorId(checkpoint)) + 1)))
      errors.push(`${path}/flowPosition : position de flux invalide.`);
    for (const id of checkpointEvidenceIds(checkpoint)) {
      const question = blocks.get(id);
      if(question?.visible===false)errors.push(`${path} : preuve masquée ${id}. Rendez la preuve visible ou adaptez le checkpoint.`);
      if (!question || (checkpoint.evidenceBlockIds ? !isEvidenceBlock(question) : question.type !== "question")) errors.push(`${path}/${checkpoint.evidenceBlockIds ? "evidenceBlockIds" : "questionIds"} : bloc de preuve invalide ${id}.`);
      else if (!("competencyIds" in question) || !question.competencyIds?.includes(checkpoint.competencyId))
        errors.push(`${path}/${checkpoint.evidenceBlockIds ? "evidenceBlockIds" : "questionIds"} : ${id} ne fournit pas de preuve pour ${checkpoint.competencyId}.`);
    }
    if (checkpoint.evidenceBlockIds && checkpoint.questionIds &&
        (JSON.stringify(checkpoint.evidenceBlockIds) !== JSON.stringify(checkpoint.questionIds) || checkpoint.positionAfterBlockId !== checkpoint.positionAfterQuestionId))
      errors.push(`${path} : références anciennes et nouvelles contradictoires.`);
    if (checkpoint.rubric && Object.values(checkpoint.rubric).some((criterion) => !criterion.trim()))
      errors.push(`${path}/rubric : les quatre critères doivent être renseignés.`);
  }
  if (new Set(rules.map((r) => r.competencyId)).size !== rules.length)
    errors.push(
      "/interactive/competencyRules : plusieurs groupes pour la même compétence.",
    );
  rules.forEach((r, i) => {
    if (
      r.thresholds &&
      !(r.thresholds.A >= r.thresholds.B && r.thresholds.B >= r.thresholds.C)
    )
      errors.push(
        `/interactive/competencyRules/${i}/thresholds : respecter A ≥ B ≥ C.`,
      );
  });
  return [...new Set(errors)];
}
/** Strict import: never coerce a version, strip fields, or repair invalid AI input silently. */
export function parseInteractiveActivityPackage(
  text: string,
  allowedCompetencyIds?: string[],
): InteractiveActivityPackage {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : "JSON invalide";
    const position = message.match(/position (\d+)/)?.[1];
    const prefix = text
      .slice(0, position ? Number(position) : text.length)
      .split("\n");
    throw new Error(
      `Ligne ${prefix.length}, colonne ${prefix.at(-1)!.length + 1} : ${message}`,
    );
  }
  const errors = validateInteractiveActivityPackage(
    value,
    allowedCompetencyIds,
  );
  if (errors.length) throw new Error(errors.join("\n"));
  return value as InteractiveActivityPackage;
}
/** JSON value copy: subsequent edits cannot mutate the revision captured by a student copy. */
export function snapshotInteractiveActivityPackage(
  value: InteractiveActivityPackage,
): InteractiveActivityPackage {
  return structuredClone(value);
}
export function createInteractiveActivityDraft(
  value: InteractiveActivityPackage,
): InteractiveActivityPackage {
  const draft = snapshotInteractiveActivityPackage(value);
  draft.interactive.revision += 1;
  draft.interactive.status = "draft";
  return draft;
}

